import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogTitle from '@material-ui/core/DialogTitle'
import { makeStyles, Theme } from '@material-ui/core/styles'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import { replaceAll } from 'pages/ProjectsPage/WorkspacePage/components/Common/utils'
import React, { useEffect } from 'react'
import useBetaWorkspaceToggle from 'pages/ProjectsPage/WorkspacePage/components/WorkspaceRightPane/hooks/useBetaWorkspaceToggle'

interface StyleProps {
    isBetaWorkspaceToggleEnabled: boolean
}
const useStyles = makeStyles((theme: Theme) => ({
    dialogTitle: {
        '& .MuiTypography-h6': {
            color: '#304959',
            fontSize: pxToRem(34),
        },
        '&.MuiDialogTitle-root': {
            paddingLeft: theme.spacing(0),
            paddingRight: theme.spacing(0),
        },
    },
    actionButton: {
        color: (props: StyleProps) =>
            props.isBetaWorkspaceToggleEnabled ? '#1899D4' : '#A78966',
        fontSize: pxToRem(18),
        width: 108,
        marginBottom: 16,
    },
    promptText: {
        paddingTop: theme.spacing(1),
        fontSize: pxToRem(14),
        color: theme.palette.common.black,
    },
    textField: {
        marginTop: theme.spacing(3),
    },
    dialog: {
        '& .MuiDialog-paperScrollPaper': {
            paddingLeft: theme.spacing(3),
            paddingRight: theme.spacing(3),
        },
    },
    dialogAction: {
        '&.MuiDialogActions-root': {
            paddingLeft: theme.spacing(0),
            paddingRight: theme.spacing(0),
        },
    },
}))

export interface StringInputPromptProps {
    /**
     * Determines whether dialog is open.
     */
    openDialog: boolean

    /**
     * Callback when dialog is cancelled
     */
    handleCancel?: VoidFunction

    /**
     * Callback when dialog is OK'ed
     */
    handleOK: (value: string) => void

    /**
     * Text for the dialog title.
     */
    dialogHeading: string

    /*
     * Defines the prompt placed above the input textbox.
     */
    inputPrompt: string

    /*
     * Defines the hint/label for the input textbox.
     */
    inputTextHint?: string

    /*
     * Value for the input text.
     */
    inputText?: string

    /*
     * Function for setting the input text.
     */
    setInputText?: (value: string) => void

    /*
     * Defines the text for cancel action button on confirmation dialog. Default: Cancel
     */
    cancelText?: string

    /*
     * Defines the text for confirmation action button  on confirmation dialog. Default: OK
     */
    okText?: string

    /*
     * Minimum number of characters required for input to be accepted. Default: 1
     */
    minLength?: number

    /*
     * Maximum number of characters allowed. Default: 200
     */
    maxLength?: number

    /*
     * Characters to strip from the input.
     */
    invalidCharacters?: string[]
}
const StringInputPrompt: React.FC<StringInputPromptProps> = ({
    openDialog,
    handleCancel,
    handleOK,
    dialogHeading,
    inputPrompt,
    inputTextHint,
    inputText = '',
    setInputText,
    cancelText = 'Cancel',
    okText = 'OK',
    maxLength = 200,
    minLength = 1,
    invalidCharacters,
}) => {
    const isBetaWorkspaceToggleEnabled = useBetaWorkspaceToggle()
    const classes = useStyles({ isBetaWorkspaceToggleEnabled })
    const validateInput = React.useCallback((s: string) => {
        return s.trim().length >= minLength
    }, [])
    const [isInputValid, setIsInputValid] = React.useState(
        validateInput(inputText)
    )

    const handleKeyDown = React.useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (event.key === 'Enter') {
                event.preventDefault()
                handleOKClick()
            } else if (event.key === 'Escape' && handleCancel) {
                event.preventDefault()
                handleCancelClick()
            }
        },
        [handleOK, handleCancel, inputText, isInputValid]
    )

    const handleOKClick = React.useCallback(() => {
        isInputValid && handleOK && handleOK(inputText.trim())
    }, [inputText, handleOK, isInputValid])

    const handleCancelClick = React.useCallback(() => {
        handleCancel && handleCancel()
    }, [inputText, handleCancel])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let val = e.target.value
        if (invalidCharacters?.length) {
            invalidCharacters.map((seq) => (val = replaceAll(val, seq, '')))
        }
        val = val.trimStart().substring(0, maxLength)
        setIsInputValid(validateInput(val))
        setInputText && setInputText(val)
    }

    // select all text on focus
    const handleFocus = (
        e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
    ): void => e.target.select()

    useEffect(() => setIsInputValid(validateInput(inputText)))

    return (
        <Dialog
            open={openDialog}
            data-testid="stringInputPrompt"
            className={classes.dialog}
        >
            <DialogTitle className={classes.dialogTitle}>
                {dialogHeading}
            </DialogTitle>
            <Typography className={classes.promptText}>
                {inputPrompt}
            </Typography>
            <TextField
                className={classes.textField}
                autoFocus
                margin="dense"
                data-testid="stringInputPromptTextField"
                label={inputTextHint}
                fullWidth
                variant="outlined"
                value={inputText}
                onFocus={handleFocus}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                inputProps={{ maxLength: maxLength }}
            />
            <DialogActions className={classes.dialogAction}>
                <Button
                    data-testid="stringInputPromptCancelButton"
                    className={classes.actionButton}
                    onClick={handleCancelClick}
                >
                    {cancelText}
                </Button>
                <Button
                    data-testid="stringInputPromptOKButton"
                    onClick={handleOKClick}
                    className={classes.actionButton}
                    disabled={!isInputValid}
                >
                    {okText}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default StringInputPrompt
